import React, { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import { Action, Dispatch } from 'redux';

import { CallStatus } from '../../lib/enums';
import lang from '../../lib/language';
import { RootState } from '../../lib/store';
import { PaymentCall } from '../../lib/store/payment/types';
import { getAccountSummary, getAccountTradeHistory, getOpenPositions } from '../../lib/store/reporting';
import { PENDING_ORDERS_REQUEST_TENNSTATUS } from '../../lib/store/reporting/constants';
import { AccountTradeHistoryPayloadData } from '../../lib/store/reporting/types';
import { getCurrentAccount, isUserVerifiedStatus } from '../../lib/store/selectors';
import { getBuyingPower } from '../../lib/store/trading';
import { manageSubscriptions } from '../../lib/util/TradingHelpers';

import { BaseModal, Button, NotVerifiedNotification, SymbolDetailsNotActiveBox } from '../../components';
import Chart from '../../components/Chart/NetComponentsChart/Chart';
import OrderResponseManager from '../../components/OrderResponseManager/OrderResponseManager';
import { useBrowserWidth, useIsMobile } from '../../hooks';
import { BASE_WIDTH_SYMBOL_DETAILS, MAX_MOBILE_WIDTH } from '../../util/constants';

import CashPortfolioChart from './CashPortfolioChart/CashPortfolioChart';
import AccountBreakDown from './TableContent/MyAccountTableContent';

import styles from './MyAccount.module.scss';

type Props = ReduxProps;

const MyAccount = (props: Props) => {
  const {
    accountReferenceId,
    individualData,
    hasDeposit,
    isUserVerified,
    isPaymentsReady,
    transactions,
    getTradeHistory,
    getPositions,
    getSummary,
    fetchBuyingPowerAndPutGlobalState,
  } = props;

  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isOpenPieChartModal, setIsOpenPieChartModal] = useState<boolean>(false);
  const [isUserReadyToTrade, setIsUserReadyToTrade] = useState<boolean>(true);
  const isMobile = useIsMobile();
  const { browserWidthSize } = useBrowserWidth();
  const isSmallWebScreen = browserWidthSize <= BASE_WIDTH_SYMBOL_DETAILS;

  useEffect(() => {
    if (accountReferenceId) {
      getPositions();
      getTradeHistory({ tenNStatus: PENDING_ORDERS_REQUEST_TENNSTATUS, rowsPerPage: 30, isPendingOrdersCall: true });
      getSummary();
      setIsInitialized(true);
    }
  }, [accountReferenceId, getPositions, getSummary, getTradeHistory]);

  useEffect(() => {
    manageSubscriptions('my-account', null, true, true);
  }, []);

  useEffect(() => {
    if ((!isUserVerified || isPaymentsReady) && !hasDeposit && isEmpty(transactions) && individualData) {
      setIsUserReadyToTrade(false);
    }
  }, [isUserVerified, isPaymentsReady, transactions, hasDeposit, individualData]);

  useEffect(() => {
    if (accountReferenceId) {
      fetchBuyingPowerAndPutGlobalState();
    }
  }, [accountReferenceId]);// eslint-disable-line react-hooks/exhaustive-deps

  const handlePopUp = (value: boolean) => {
    setIsOpenPieChartModal(value);
  };

  const renderCashPortfolioChart = (): JSX.Element => (isSmallWebScreen ? (
    <BaseModal
      customStyle={browserWidthSize < MAX_MOBILE_WIDTH ? {
        paddingBottom: 90,
        margin: 0,
        width: window.innerWidth,
        marginTop: window.innerWidth / 2.5,
        position: 'fixed',
        alignSelf: 'flex-end',
      } : {}}
      className={styles.pieChartModalWrpper}
      isOpen={isOpenPieChartModal}
      toggle={() => handlePopUp(false)}
    >
      <div className={styles.pieChartModalContent}>
        <CashPortfolioChart isSmallScreen={browserWidthSize <= BASE_WIDTH_SYMBOL_DETAILS} />
      </div>
      <Button onClick={() => handlePopUp(false)}>
        {lang.commonClose()}
      </Button>
    </BaseModal>
  ) : (
    <div className={styles.chashBalance}>
      <CashPortfolioChart />
    </div>
  ));

  return (
    <div className={classNames(styles.myAccountWrapper,
      { [styles.noMargin]: isMobile })}
    >
      <OrderResponseManager />
      <div className={styles.wrapperLayout}>
        <div className={styles.wrapperFirstSection}>
          { isSmallWebScreen
            && individualData
            && transactions.length === 0
            && (
            <div className={classNames(styles.notVerified,
              { [styles.noMargin]: isMobile })}
            >
              <NotVerifiedNotification
                individual={individualData}
                hasUserName={false}
                isPaymentsReady={isPaymentsReady}
              />
            </div>
            )}
          <div className={styles.wrapperAccount}>
            <Chart
              isSmallScreen={isSmallWebScreen}
              handleChartPopUp={(value) => handlePopUp(value)}
            />
          </div>
        </div>
        <div className={styles.wrapperSecondSection}>
          {!isUserReadyToTrade && !isSmallWebScreen
            && <SymbolDetailsNotActiveBox className={['d-none', 'd-lg-block', styles.notVerified].join(' ')} />}
          {renderCashPortfolioChart()}
        </div>
      </div>
      <AccountBreakDown isMyAccountInitiliazed={isInitialized} isUserVerified={isUserReadyToTrade} />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  accountReferenceId: getCurrentAccount(state),
  individualData: state.crm.individualExtendedInfo,
  companyLogoAndName: state.fundamental.companyLogoAndName,
  hasDeposit: state.payment.hasDeposit,
  isUserVerified: isUserVerifiedStatus(state.crm.individualExtendedInfo),
  isPaymentsReady: state.payment.statusByCall[PaymentCall.getPaymentsAll]?.status === CallStatus.READY,
  transactions: state.payment.paymentsAll.data,
});
const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getTradeHistory: (data: AccountTradeHistoryPayloadData) => dispatch(getAccountTradeHistory(data)),
  getPositions: () => dispatch(getOpenPositions()),
  getSummary: () => dispatch(getAccountSummary()),
  fetchBuyingPowerAndPutGlobalState: () => dispatch(getBuyingPower()),
});
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(MyAccount);
